import { Nullable } from './global.definition';
import { PagedQuery, PagedResponse, Pagination, Response } from './rest.definition';

export type GetKaryawanResponse = PagedResponse<{
  uuid: string;
  name: string;
  code: string;
  image: Nullable<string>;
  is_manager: boolean;
  is_leader: boolean;
  jabatan: Nullable<{
    code: string;
    name: string;
  }>;
  divisi: Nullable<{
    code: string;
    name: string;
  }>;
  sub_divisi: Nullable<{
    code: string;
    name: string;
  }>;
  unit_kerja: Nullable<{
    name: string;
  }>;
  lokasi: Nullable<{
    name: string;
  }>;
  group: Nullable<{
    name: string;
  }>;
  kontrak: Nullable<{
    type: string;
    periode: number;
    periode_type: string;
  }>;
  fingerprint_verified: boolean;
  has_jadwal: boolean;
  saldo_cuti: number;
  created_at: Date;
}>;

export type GetKaryawanQuery = PagedQuery & {
  status?: '0' | '1';
  divisi_id?: string;
  sub_divisi_id?: string;
  unit_kerja_id?: string;
  lokasi_id?: string;
  jabatan_id?: string;
};

export type CreateKaryawanForm = {
  // Step 1
  image: string;
  name: string;
  nickname: string;
  nationality: string;
  place_of_birth: string;
  birthday: string;
  identity_number: string | null;
  religion: string;
  gender: string;
  address_original: string;
  blood_type: string;
  npwp_number: string | null;
  ktp_image: string;
  npwp_image: string;
  address_current: string;
  banks: {
    bank_name: string;
    account_number: string | null;
    account_name: string;
    is_primary: boolean;
  }[];
  kontak_darurat: {
    name: string;
    phone: string | null;
    phone_ext: string;
    relation: string;
    address: string;
  }[];
  nama_faskes: string;
  kode_faskes: string | null;
  weight: number | null;
  height: number | null;
  shirt_size: string;
  pants_size: number | null;
  shoe_size: number | null;
  has_medical_history: boolean;
  medical_history: string;

  // Step 2
  martial_status: string;
  has_children: boolean;
  children_count: number | null; // [REQUIRED IF has_children = true]
  living_status: string; // Rumah Pribadi | Keluarga | Kontrak/Sewa/Kost
  ownership_status: string; // KPR | LUNAS -> [REQUIRED IF living_status = "Rumah Pribadi"]
  rent_amount: number | null; // [REQUIRED IF living_status = "Kontrak/Sewa/Kost"]

  // Step 3
  phone: string | null;
  phone_ext: string;
  email: string;
  instagram_url: string;
  facebook_url: string;
  tiktok_url: string;

  // Step 4
  divisi_id: string;
  sub_divisi_id: string;
  unit_kerja_id: string;
  level_id: string;
  lokasi_id: string;
  kontrak_id: string;
  join_date: string; // yyyy-MM-dd
  saldo_cuti_awal: number | null;

  // Step 5
  kk_number: string | null;
  kk_image: string;
  keluarga: {
    name: string;
    phone: string | null;
    phone_ext: string;
    relation: string;
    gender: string;
  }[];
};

export type CreateKaryawanResponse = Response<{
  uuid: string;
}>;

export type GetKaryawanDetailResponse = Response<{
  uuid: string;
  name: string;
  code: string;
  nickname: Nullable<string>;
  image: Nullable<string>;
  phone_ext: string;
  phone: string;
  email: Nullable<string>;
  is_manager: boolean;
  jabatan: {
    id: string;
    code: string;
    name: string;
  };
  divisi: Nullable<{
    id: number;
    code: string;
    name: string;
  }>;
  sub_divisi: Nullable<{
    id: number;
    code: string;
    name: string;
  }>;
  unit_kerja: Nullable<{
    id: number;
    name: string;
  }>;
  lokasi: Nullable<{
    id: number;
    name: string;
  }>;
  group: Nullable<{
    uuid: string;
    name: string;
  }>;
  kontrak: Nullable<{
    id: number;
    type: string;
    code: string;
    name: string;
    periode: Nullable<number>;
    periode_type: Nullable<string>;
  }>;
  status: number; // 0: nonaktif, 1: aktif
  fingerprint_verified: boolean;
  birthday: string;
  place_of_birth: string;
  blood_type: string;
  martial_status: string;
  gender: string;
  religion: string;
  nationality: string;
  address_original: string;
  identity_number: string;
  npwp_number: Nullable<string>;
  npwp_image: Nullable<string>;
  ktp_image: Nullable<string>;
  instagram_url: Nullable<string>;
  facebook_url: Nullable<string>;
  address_current: string;
  tiktok_url: Nullable<string>;
  living_status: string;
  ownership_status: Nullable<string>;
  rent_amount: Nullable<number>;
  has_children: boolean;
  children_count: Nullable<number>;
  created_at: string;
  join_date: string;
  tanggal_mulai: Nullable<string>; // untuk kontrak yang diperpanjang
  tanggal_selesai: Nullable<string>; // untuk kontrak yang diperpanjang
  bank: {
    bank_name: string;
    account_number: string;
    account_name: string;
    is_primary: boolean;
  }[];
  kontak_darurat: {
    name: string;
    phone_ext: string;
    phone: string;
    relation: string;
    address: string;
  }[];
  kk_number: string;
  kk_image: string;
  keluarga: {
    name: string;
    phone_ext: string;
    phone: string;
    gender: string;
    relation: string;
  }[];
  nama_faskes: string;
  kode_faskes: string;
  saldo_cuti_awal: number;
  weight: Nullable<string>;
  height: Nullable<string>;
  shirt_size: Nullable<string>;
  pants_size: Nullable<string>;
  shoe_size: Nullable<string>;
  medical_history: Nullable<string>;
  resign: Nullable<{
    alasan_resign: string;
    tanggal_resign: string;
    catatan_hr: Nullable<string>;
    lampiran: { file: string; name: string }[];

    created_at: Nullable<string>;
    created_by: Nullable<string>;
  }>;
}>;

export type GetKaryawanSummaryQuery = {
  start_date: string;
  end_date: string;
};

export type GetKaryawanSummaryResponse = Response<{
  total_hadir: number;
  total_hadir_tepat_waktu: number;
  total_tidak_hadir: number;
  total_tidak_hadir_tanpa_izin: number;
  total_tidak_hadir_dengan_izin: number;
  total_terlambat: number;
  total_terlambat_tanpa_izin: number;
  total_terlambat_dengan_izin: number;
  total_pulang_cepat: number;
  total_pulang_cepat_tanpa_izin: number;
  total_pulang_cepat_dengan_izin: number;
}>;

export type GetKaryawanAbsensiQuery = {
  start_date: string;
  end_date: string;
};

export type GetKaryawanAbsensiResponse = Response<
  Pagination & {
    data: {
      tanggal: string;
      clock_in: Nullable<{
        jam: string;
        status: string;
        selisih: Nullable<string>;
      }>;
      clock_out: Nullable<{
        jam: string;
        status: string;
        selisih: Nullable<string>;
      }>;
      is_work_day: boolean;
      status: string; // Belum Hadir, Hadir, Tidak Hadir
      status_clock_in: Nullable<string>;
      status_clock_out: Nullable<string>;
      adjusted_clock_in: Nullable<string>;
      adjusted_clock_out: Nullable<string>;
      keterangan: Nullable<string>;
      pola: Nullable<{
        name: string;
        // Jadwal
        pola: Nullable<{
          name: string;
        }>;
      }>;
      libur: Nullable<{
        name: string;
        jenis_libur: string;
      }>;
      shift: Nullable<{
        name: string;
        istirahat_count: number;
        check_in: string;
        check_out: string;
        auto_check_out_limit: number;
        auto_check_out_limit_type: string;
        late_tolerance: number;
        late_tolerance_type: string;
      }>;
      pengajuan_count: Nullable<{
        disetujui?: number;
        ditolak?: number;
        diajukan?: number;
      }>;
    }[];
  }
>;

export type GetKaryawanCutiQuery = PagedQuery & {
  bulan: string;
  tahun: string;
};

export type GetKaryawanCutiResponse = Response<{
  mutasi: Pagination & {
    data: {
      tipe: string; // penambahan_tahunan | pengurangan | penyesuaian_tambah
      tipe_mutasi: string; // debit | kredit | netral
      jumlah: number;
      created_at: string;
      expired_at: string;
      saldo_akhir: number;
      no_pengajuan: Nullable<string>;
      keterangan: Nullable<string>;
    }[];
  };
  summary: {
    penambahan: number;
    pengurangan: number;
    penyesuaian_bersih: number;
    periode_mulai: string;
    saldo_awal: number;
    sisa_saldo: number;
    terpakai: number;

    // TODO:
    updated_at: Nullable<string>;
  };
}>;

export type GetKaryawanDokumenResponse = Response<
  {
    file: string;
    name: string;
    created_at: string;
    size_mb: Nullable<string>;
    created_by: Nullable<string>;
  }[]
>;

export type GetKaryawanCalendarQuery = {
  bulan: number;
  tahun: number;
};

export type GetKaryawanCalendarResponse = Response<{
  absensi: {
    tanggal: string;
    status: string; // Libur
  }[];
  pengajuan: {
    tanggal: string;
    name: string;
  }[];
  libur: Nullable<Record<string, { name: string }[]>>;
}>;

export type GetKaryawanShiftResponse = Response<{
  clock_in: Nullable<string>;
  clock_out: Nullable<string>;
  libur: {
    name: string;
    jenis_libur: string;
  }[];
  shift: Nullable<{
    name: string;
    color: string;
    check_in: string;
    check_out: string;
    status_clock_in: Nullable<string>;
    status_clock_out: Nullable<string>;
    istirahat: {
      break_start: string;
      break_end: string;
    }[];
  }>;
}>;

export type ActiveKaryawanForm = {
  tanggal_masuk_kembali: string;
  divisi_id: string;
  sub_divisi_id: string;
  unit_kerja_id: string;
  group_id: string;
  kontrak_id: string;
  catatan_hr: string;
  lampiran: (string | null)[];
};

export type InactiveKaryawanForm = {
  tanggal_resign: string;
  alasan_resign: string;
  catatan_hr: string;
  lampiran: (string | null)[];
};

export type GetKaryawanFingerprintResponse = Response<{
  jari: {
    uuid: string;
    jari: string;
    tangan: string;
  }[];
}>;

export type CreateKaryawanFingerprintForm = {
  jari: string;
  tangan: string;
  templates: string[];
};

export type GetKaryawanAbsensiDetailResponse = Response<{
  status: string;
  tanggal: string;
  updated_at?: string;
  clock_in: Nullable<{
    jam: string;
    metode: string;
    selisih: Nullable<string>;
    status: string;
  }>;
  clock_out: Nullable<{
    jam: string;
    metode: string;
    selisih: Nullable<string>;
    status: string;
  }>;
  karyawan: {
    name: string;
    jabatan: string;
    divisi: string;

    // TODO:
    image: Nullable<string>;
    lokasi: Nullable<string>;
    sub_divisi: Nullable<string>;
    join_date: Nullable<string>;
  };
  shift: Nullable<{
    name: string;
    color: string;
    check_in: string;
    check_out: string;
    late_tolerance: number;
    late_tolerance_type: string; // hour, minute, second
    early_check_out: number;
    early_check_out_type: string; // hour, minute, second
    start_check_in?: string;
    auto_check_out?: string;

    breaks: Nullable<{ check_in: string; check_out: string }[]>;
  }>;
  libur: Nullable<
    {
      name: string;
      potong_cuti: boolean;

      tanggal_mulai: string;
      tanggal_selesai: string;
    }[]
  >;
  durasi_aktual: {
    jam: number;
    menit: number;
    detik: number;
  };
  rincian_durasi: {
    durasi_istirahat: { jam: number; menit: number; detik: number };
    durasi_terjadwal: { jam: number; menit: number; detik: number };
    selisih_pulang_awal: { jam: number; menit: number; detik: number };
    terlambat: { jam: number; menit: number; detik: number };
  };
  izin: Nullable<
    {
      tipe: string;
      untuk: string;
      no_pengajuan: string;
      keterangan: Nullable<string>;
      dibuat: string;
    }[]
  >;
  log: {
    tipe: string; // info | status | clock_in | clock_out
    waktu: string;

    // clock_in | clock_out
    metode: string; // barcode | fingerprint
    status: string;
    selisih: Nullable<string>;

    // info
    pesan: Nullable<string>;
  }[];
  potongan: Nullable<number>;
  keterangan: Nullable<string>;
}>;

export type UpdateKontrakForm = {
  kontrak_id: number | null;
  tanggal_mulai: string;
  tanggal_selesai: string;
  keterangan: string;
  file: string;
};

export const GenderType = {
  LakiLaki: 'Laki Laki',
  Perempuan: 'Perempuan',
} as const;

// export const CutiType = {
//   PENAMBAHAN_TAHUNAN: 'penambahan_tahunan',
//   PENYESUAIAN_TAMBAH: 'penyesuaian_tambah',
//   PENYESUAIAN_KURANGI: 'penyesuaian_kurangi',
//   PENGURANGAN: 'pengurangan',
//   PENGEMBALIAN: 'pengembalian',
// } as const;

// export const CutiTypeLabel: Record<string, string> = {
//   [CutiType.PENAMBAHAN_TAHUNAN]: 'Penambahan Tahunan',
//   [CutiType.PENYESUAIAN_TAMBAH]: 'Revisi Penambahan',
//   [CutiType.PENYESUAIAN_KURANGI]: 'Revisi Pengurangan',
//   [CutiType.PENGURANGAN]: 'Pengurangan',
//   [CutiType.PENGEMBALIAN]: 'Pengembalian'
// };

// export const CutiTypeMode: Record<string, 'credit' | 'debit'> = {
//   [CutiType.PENAMBAHAN_TAHUNAN]: 'credit',
//   [CutiType.PENYESUAIAN_TAMBAH]: 'credit',
//   [CutiType.PENYESUAIAN_KURANGI]: 'debit',
//   [CutiType.PENGURANGAN]: 'debit',
//   [CutiType.PENGEMBALIAN]: 'credit',
// };

export const LivingStatus = {
  PRIBADI: 'Rumah Pribadi',
  KELUARGA: 'Keluarga',
  KONTRAK: 'Kontrak/Sewa/Kost',
} as const;
